import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { AlertTriangle, ArrowLeft, Lock, Check, Clock, GripVertical } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogAction,
} from "../components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function HobbiesPage({ user, program: programProp, onBack }) {
  const [config, setConfig] = useState(null);
  const [selections, setSelections] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [confirmedCamper, setConfirmedCamper] = useState(null);
  const [selectedCamper, setSelectedCamper] = useState(null);
  const [capWarning, setCapWarning] = useState(null);  // {title, body} for popup

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const configParams = programProp ? { program: programProp } : {};
      const selectionsParams = programProp ? { program: programProp } : {};
      const [configRes, selectionsRes] = await Promise.all([
        axios.get(`${API}/hobbies/config`, { headers, params: configParams }),
        axios.get(`${API}/hobbies/selections`, { headers, params: selectionsParams }),
      ]);
      setConfig(configRes.data);
      setSelections(selectionsRes.data);

      // Fetch roster for the active week's day (wednesday for mariners/intensive, monday for navigators)
      const activeWeek = configRes.data.current_week;
      if (activeWeek) {
        const dayKey = configRes.data.day_key || "wednesday";
        const rosterDate = activeWeek[dayKey] || activeWeek.wednesday || activeWeek.monday;
        const rosterRes = await axios.get(`${API}/roster/today`, {
          headers,
          params: { date: rosterDate },
        });
        setRoster(rosterRes.data?.campers || []);
      }
    } catch {
      toast.error("Failed to load hobbies data");
    } finally {
      setLoading(false);
    }
  }, [programProp]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelect = async (camperId, camperName, activity) => {
    setSubmitting(camperId);
    try {
      const body = { camper_id: camperId, activity };
      if (programProp) body.program = programProp;
      await axios.post(
        `${API}/hobbies/select`,
        body,
        { headers }
      );
      setConfirmedCamper(camperId);
      setTimeout(() => setConfirmedCamper(null), 1500);
      toast.success(`${camperName} -> ${activity}`);
      // Refresh selections
      const selectionsParams = programProp ? { program: programProp } : {};
      const res = await axios.get(`${API}/hobbies/selections`, { headers, params: selectionsParams });
      setSelections(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to submit selection";
      const status = err.response?.status;
      // Resolve the effective program: prop overrides (intensive button passes "intensive"),
      // otherwise fall back to whatever /api/hobbies/config returned (e.g. "navigator").
      const effectiveProgram = programProp || config?.program;
      const isNavPath = effectiveProgram === "navigator" || effectiveProgram === "intensive";
      if (status === 409 && isNavPath) {
        const label = effectiveProgram === "intensive" ? "Intensive" : "Hobby";
        setCapWarning({
          title: `This ${label} is full`,
          body: detail,
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(null);
    }
  };

  const isOpen = config?.current_week != null;
  const currentWeekNum = config?.current_week?.week;
  const isCatchUp = !!config?.current_week?.catch_up;
  const activities = config?.activities || [];
  const division = config?.division;
  const program = config?.program || "mariner";
  const isNavigator = program === "navigator";
  const isIntensive = program === "intensive";
  // Navigator hobbies & intensives: can repeat the same activity across weeks
  const allowRepeat = isNavigator || isIntensive;
  const dayKey = config?.day_key || "wednesday";
  const dayLabel = isNavigator ? "Mondays" : "Wednesdays";
  const timeBlock = (isNavigator || isIntensive)
    ? (config?.time || "")
    : (division === "Mariner B" ? config?.boys_time : config?.girls_time);
  const totalWeeks = config?.weeks?.length || 6;
  const pageTitle = isIntensive ? "Navigator Intensives" : (isNavigator ? "Navigator Hobbies Pick of the Week" : "Hobbies Pick of the Week");

  // Build lookup: camper_id -> {week_number: {activity, drama_locked}}
  const selMap = {};
  selections.forEach((s) => {
    if (!selMap[s.camper_id]) selMap[s.camper_id] = {};
    selMap[s.camper_id][s.week_number] = s.activity_selected;
  });

  // Current week selection for each camper
  const currentSelections = {};
  // Track Drama-locked campers (week 1 Drama in intensive locks all 6 weeks)
  const dramaLockedCampers = new Set();
  selections.forEach((s) => {
    if (s.week_number === currentWeekNum) {
      currentSelections[s.camper_id] = s.activity_selected;
    }
    if (isIntensive && s.week_number === 1 && s.activity_selected === "Drama") {
      dramaLockedCampers.add(s.camper_id);
    }
  });

  // Activities already used by each camper (any week) — used for mariner (cannot
  // repeat any activity) AND navigator once-per-summer activities (Cooking,
  // Pet Care/Nature, Tech Lounge).
  const usedActivities = {};
  const oncePerSummerSet = new Set(config?.once_per_summer_activities || []);
  if (!allowRepeat) {
    // Mariner: every activity is one-time.
    selections.forEach((s) => {
      if (!usedActivities[s.camper_id]) usedActivities[s.camper_id] = {};
      usedActivities[s.camper_id][s.activity_selected] = s.week_number;
    });
  } else if (isNavigator && oncePerSummerSet.size > 0) {
    // Navigator hobbies: only restrict the once-per-summer subset.
    selections.forEach((s) => {
      if (!oncePerSummerSet.has(s.activity_selected)) return;
      if (!usedActivities[s.camper_id]) usedActivities[s.camper_id] = {};
      usedActivities[s.camper_id][s.activity_selected] = s.week_number;
    });
  }

  // Per-group staggered slot state (Navigator hobbies + intensives).
  // Backend sends `slot_schedule` (per-group open times for the current week),
  // `my_slot_open` (when the logged-in group's slot opens), `window_open`,
  // `window_close`, and `week_is_staggered`. For non-staggered weeks (e.g.
  // Mariner, Navigator Hobby Week 1) we fall back to legacy behaviour.
  const slotSchedule = config?.slot_schedule || [];
  const weekIsStaggered = !!config?.week_is_staggered;
  const mySlotOpenIso = config?.my_slot_open;
  const windowOpenIso = config?.window_open;
  const windowCloseIso = config?.window_close;
  const myGroupCode = (user?.group_code || "").toUpperCase();
  const userType = user?.user_type;
  const disabledActivities = new Set(config?.current_week?.disabled_activities || []);

  // Parse a naive ET ISO into a real Date for comparison. Browser will treat
  // it as local time; we offset by the difference from the user's TZ to ET to
  // get a reliable wall-clock-in-ET comparison.
  const parseEt = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso + "-04:00"); // Eastern in summer (EDT). Acceptable jitter for slot gates.
    } catch { return null; }
  };
  const nowMs = Date.now();
  const mySlotOpenMs = parseEt(mySlotOpenIso)?.getTime() ?? null;
  const windowCloseMs = parseEt(windowCloseIso)?.getTime() ?? null;

  // My-slot state: "pending" before my slot opens, "open" between my slot and
  // window_close, "closed" after window_close. For non-staggered weeks (e.g.
  // Week 1 Hobbies) we use window_open / window_close directly.
  let mySlotState = "open";
  if (weekIsStaggered && mySlotOpenMs != null) {
    if (nowMs < mySlotOpenMs) mySlotState = "pending";
    else if (windowCloseMs != null && nowMs >= windowCloseMs) mySlotState = "closed";
  } else if (windowCloseMs != null && nowMs >= windowCloseMs) {
    mySlotState = "closed";
  }
  // Block selection if my staggered slot hasn't opened yet. Admins / supervisors
  // (no group code) are never blocked.
  const blockedByPriority = weekIsStaggered && userType === "counselor" && mySlotState === "pending";

  // Format a naive ET ISO into "10:30 AM" wall-clock label
  const fmtSlotLabel = (iso) => {
    if (!iso) return "";
    try {
      const [, time] = iso.split("T");
      const [h, m] = time.split(":").map(Number);
      const hh = h % 12 === 0 ? 12 : h % 12;
      const ampm = h >= 12 ? "PM" : "AM";
      return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
    } catch { return iso; }
  };
  // Format full close label (e.g. "Thursday at 2:00 PM")
  const fmtCloseLabel = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso + "-04:00");
      const dow = d.toLocaleDateString("en-US", { weekday: "long", timeZone: "America/New_York" });
      return `${dow} at ${fmtSlotLabel(iso)}`;
    } catch { return iso; }
  };
  const mySlotLabel = fmtSlotLabel(mySlotOpenIso);
  const windowCloseLabel = fmtCloseLabel(windowCloseIso);
  const windowOpenLabel = fmtSlotLabel(windowOpenIso);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onBack} className="px-2 gap-1" data-testid="hobbies-back-btn">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs font-medium">Dashboard</span>
            </Button>
            <div>
              <h1 className="text-sm font-bold text-violet-700">{pageTitle}</h1>
              <p className="text-[10px] text-muted-foreground">
                {isNavigator || isIntensive ? "" : (division === "Mariner B" ? "Boys - " : "Girls - ")}{dayLabel} {timeBlock}
              </p>
            </div>
          </div>
          {isOpen ? (
            isCatchUp ? (
              <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs" data-testid="hobby-window-catchup">
                <Clock className="w-3 h-3 mr-1" /> Week {currentWeekNum} Catch-Up
              </Badge>
            ) : (
              <Badge className="bg-green-100 text-green-700 border-green-300 text-xs" data-testid="hobby-window-open">
                <Clock className="w-3 h-3 mr-1" /> Week {currentWeekNum} Open
              </Badge>
            )
          ) : (
            <Badge variant="secondary" className="text-xs" data-testid="hobby-window-closed">
              <Lock className="w-3 h-3 mr-1" /> Window Closed
            </Badge>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-3 sm:p-4 space-y-4">
        {/* Week schedule overview */}
        <div className="flex gap-1.5 overflow-x-auto pb-2">
          {(config?.weeks || []).map((w) => (
            <div
              key={w.week}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border ${
                w.status === "open"
                  ? "bg-green-100 text-green-700 border-green-300"
                  : w.status === "past"
                  ? "bg-gray-100 text-gray-500 border-gray-200"
                  : "bg-violet-50 text-violet-400 border-violet-200"
              }`}
              data-testid={`hobby-week-pill-${w.week}`}
            >
              Wk {w.week} - {new Date((w[dayKey] || w.wednesday || w.monday) + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric" })}
            </div>
          ))}
        </div>

        {!isOpen ? (
          /* Window closed state */
          <Card className="border-2 border-dashed border-gray-200">
            <CardContent className="p-6 text-center">
              <Lock className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-muted-foreground mb-1">Selection Window Closed</h3>
              <p className="text-sm text-muted-foreground">
                {isIntensive
                  ? "Intensive selections open the Monday of the activity week and close Tuesday at 2:00 PM ET."
                  : isNavigator
                  ? "Hobby selections open the prior Wednesday and close the prior Thursday at 2:00 PM ET. Week 1 is open Monday Jun 29 morning only (closes 10:30 AM)."
                  : "Hobby selections open Monday 12:00 AM - Tuesday 2:00 PM ET each week."}
              </p>
              {selections.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-semibold text-violet-600 uppercase">Past Selections</p>
                  {roster.length === 0 && (
                    <p className="text-xs text-muted-foreground">Roster not available for closed window.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Open window — selection UI */
          <div className="space-y-3">
            {/* Catch-up banner — the shown week's window has closed; edits go to
                THAT week only. The next week always starts fresh. */}
            {isCatchUp && (() => {
              const nextWeek = (config?.weeks || []).find(w => w.status === "future");
              let opensLabel = "";
              if (nextWeek?.window_open) {
                try {
                  const d = new Date(nextWeek.window_open + "-04:00");
                  opensLabel = d.toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "short", day: "numeric" });
                } catch { /* noop */ }
              }
              return (
                <div className="p-3 rounded-xl border-2 border-amber-400 bg-amber-50" data-testid="hobby-catchup-banner">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700" />
                    <div className="text-xs text-amber-900">
                      <span className="font-bold">Week {currentWeekNum} window is closed — you're editing Week {currentWeekNum} in catch-up mode.</span>{" "}
                      Any change you make here updates Week {currentWeekNum} only.
                      {nextWeek && (
                        <> Week {nextWeek.week} opens {opensLabel || "soon"} and will start completely fresh.</>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Navigator slot panel — staggered rotation (Navigator hobbies + intensives only) */}
            {weekIsStaggered && (isNavigator || isIntensive) && (
              <div
                className={`p-3 rounded-xl border-2 ${
                  mySlotState === "pending"
                    ? "border-amber-400 bg-amber-50"
                    : mySlotState === "closed"
                    ? "border-gray-300 bg-gray-50"
                    : "border-violet-400 bg-violet-50"
                }`}
                data-testid="hobby-slot-panel"
              >
                <div className="flex items-start gap-2">
                  <Clock className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                    mySlotState === "pending" ? "text-amber-700"
                    : mySlotState === "closed" ? "text-gray-500"
                    : "text-violet-700"
                  }`} />
                  <div className="text-xs sm:text-sm flex-1 min-w-0">
                    <p className={`font-bold ${
                      mySlotState === "pending" ? "text-amber-800"
                      : mySlotState === "closed" ? "text-gray-700"
                      : "text-violet-800"
                    }`}>
                      {mySlotState === "pending"
                        ? `Your group (${myGroupCode}) opens at ${mySlotLabel}`
                        : mySlotState === "closed"
                        ? `Closed — window ended ${fmtSlotLabel(windowCloseIso)}`
                        : `Your group (${myGroupCode}) is open now`}
                    </p>
                    <p className={mySlotState === "pending" ? "text-amber-700" : mySlotState === "closed" ? "text-gray-600" : "text-violet-700"}>
                      {mySlotState !== "closed" && (<>Closes {windowCloseLabel} ET.</>)}
                    </p>
                    <div className="mt-2 grid grid-cols-4 gap-1 text-[11px]">
                      {slotSchedule.map((s) => {
                        const slotMs = parseEt(s.slot_open)?.getTime() ?? 0;
                        const isMine = s.group.toUpperCase() === myGroupCode;
                        const isOpen = nowMs >= slotMs && (windowCloseMs == null || nowMs < windowCloseMs);
                        return (
                          <div
                            key={s.group}
                            className={`px-1.5 py-1 rounded text-center border ${
                              isMine
                                ? "border-violet-500 bg-white font-bold text-violet-800"
                                : isOpen
                                ? "border-green-300 bg-green-50 text-green-700"
                                : "border-gray-200 bg-white text-gray-600"
                            }`}
                            data-testid={`slot-${s.group}`}
                          >
                            <div>{s.group}</div>
                            <div className="opacity-70">{s.label}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Week 1 hobbies — no rotation, open to all Navigator groups */}
            {!weekIsStaggered && isNavigator && currentWeekNum === 1 && (
              <div className="p-3 rounded-xl border-2 border-violet-400 bg-violet-50">
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-violet-700" />
                  <div className="text-xs sm:text-sm">
                    <p className="font-bold text-violet-800">Open now to all 8 Navigator groups</p>
                    <p className="text-violet-700">No rotation today — closes {windowCloseLabel} ET.</p>
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              {allowRepeat ? (
                <>Tap a camper, then tap an activity. Selections can be changed freely until the window closes. {isIntensive && (<span className="text-violet-700 font-medium">Drama in Week 1 locks a camper into Drama for all 6 weeks.</span>)} {isNavigator && oncePerSummerSet.size > 0 && (<span className="text-amber-700 font-medium">{[...oncePerSummerSet].join(", ")} can each be picked only once across the whole summer.</span>)}</>
              ) : (
                <>Tap a camper, then tap an activity to assign. Each camper picks <strong>one activity per week</strong> and cannot repeat across {totalWeeks} weeks.</>
              )}
            </p>

            {/* Camper list */}
            <Card>
              <CardContent className="p-3">
                <h3 className="text-xs font-bold text-violet-700 uppercase mb-2">
                  Campers ({roster.length})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {roster.map((camper) => {
                    const cid = camper.id;
                    const displayName = camper.first_name;
                    const alreadyPicked = currentSelections[cid];
                    const isSelected = selectedCamper === cid;
                    const justConfirmed = confirmedCamper === cid;
                    // Window is open here (we're in the isOpen branch). Counselors
                    // must always be able to re-open the activity grid for a kid who
                    // already has a pick this week, otherwise they can't change it.
                    // The per-activity no-repeat rule still gates the actual swap
                    // below (line ~317).
                    const clickable = true;

                    return (
                      <button
                        key={cid}
                        onClick={() => {
                          if (clickable) setSelectedCamper(isSelected ? null : cid);
                        }}
                        disabled={!clickable}
                        className={`relative p-2.5 rounded-xl border-2 text-left transition-all text-xs ${
                          justConfirmed
                            ? "border-green-400 bg-green-50 scale-105"
                            : !clickable
                            ? "border-gray-200 bg-gray-50 opacity-70 cursor-default"
                            : isSelected
                            ? "border-violet-400 bg-violet-50 shadow-md scale-[1.02]"
                            : alreadyPicked
                            ? "border-violet-200 bg-white hover:border-violet-400 hover:shadow-sm active:scale-[0.98]"
                            : "border-gray-200 bg-white hover:border-violet-300 hover:shadow-sm active:scale-[0.98]"
                        }`}
                        data-testid={`hobby-camper-${cid}`}
                      >
                        <p className="font-semibold truncate">{displayName} {camper.last_name}</p>
                        {alreadyPicked ? (
                          <div className="flex items-center gap-1 mt-1">
                            <Check className="w-3 h-3 text-green-600" />
                            <span className="text-green-700 font-medium truncate">
                              {alreadyPicked}
                              {isSelected && <span className="text-violet-600"> (tap activity to change)</span>}
                            </span>
                          </div>
                        ) : isSelected ? (
                          <p className="text-violet-600 mt-1">Pick an activity below</p>
                        ) : (
                          <p className="text-muted-foreground mt-1">Tap to select</p>
                        )}
                        {justConfirmed && (
                          <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-green-500/20 animate-pulse">
                            <Check className="w-8 h-8 text-green-600" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Activity tiles */}
            {selectedCamper && (
              <Card className="border-violet-200">
                <CardContent className="p-3">
                  <h3 className="text-xs font-bold text-violet-700 uppercase mb-2">
                    Activities for {roster.find(c => c.id === selectedCamper)?.preferred_name || roster.find(c => c.id === selectedCamper)?.first_name}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {activities.map((act) => {
                      const usedWeek = usedActivities[selectedCamper]?.[act];
                      const isUsed = usedWeek != null;
                      const isCurrent = currentSelections[selectedCamper] === act;
                      // Intensive Drama 6-week lock: if camper chose Drama in Week 1 and we're now past week 1,
                      // only Drama is selectable.
                      const isDramaLocked = isIntensive && dramaLockedCampers.has(selectedCamper) && currentWeekNum > 1;
                      const blockedByDramaLock = isDramaLocked && act !== "Drama";
                      // Bidirectional Drama lock: campers who did NOT choose Drama in Week 1
                      // cannot join Drama in any later week. The Drama tile is disabled for them.
                      const blockedByDramaJoin = isIntensive && currentWeekNum > 1 && act === "Drama" && !dramaLockedCampers.has(selectedCamper);
                      // Navigator hobbies once-per-summer activities (Cooking,
                      // Pet Care/Nature, Tech Lounge) — grey them out once used.
                      const isOnceOnly = isNavigator && oncePerSummerSet.has(act);
                      const blockedByOnceOnly = isOnceOnly && isUsed && !isCurrent;
                      // Per-week disabled activity (e.g. Gymnastics is off for Intensive Week 1).
                      const blockedByWeekDisabled = disabledActivities.has(act);
                      // Mariner: disable any used activity. Navigator: disable Drama-locked
                      // OR once-per-summer activities already taken. Plus slot-rotation gate.
                      const disabled =
                        (isUsed && !allowRepeat) ||
                        blockedByDramaLock ||
                        blockedByDramaJoin ||
                        blockedByOnceOnly ||
                        blockedByPriority ||
                        blockedByWeekDisabled ||
                        isCurrent ||
                        submitting === selectedCamper;
                      const isSubmitting = submitting === selectedCamper;
                      const camper = roster.find(c => c.id === selectedCamper);
                      const camperName = camper ? `${camper.first_name} ${camper.last_name}` : "";

                      return (
                        <button
                          key={act}
                          disabled={disabled}
                          onClick={() => handleSelect(selectedCamper, camperName, act)}
                          className={`p-3 rounded-xl border-2 text-left transition-all ${
                            isCurrent
                              ? "border-green-500 bg-green-50"
                              : blockedByWeekDisabled
                              ? "border-gray-300 bg-gray-100 opacity-60 cursor-not-allowed"
                              : blockedByDramaLock || blockedByDramaJoin || blockedByOnceOnly || blockedByPriority
                              ? "border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed"
                              : (isUsed && !allowRepeat)
                              ? "border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed"
                              : "border-violet-200 bg-white hover:border-violet-400 hover:bg-violet-50 hover:shadow-md active:scale-95 cursor-pointer"
                          }`}
                          data-testid={`hobby-activity-${act.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          <div className="flex items-start gap-2">
                            {!disabled && <GripVertical className="w-3.5 h-3.5 text-violet-300 mt-0.5 flex-shrink-0" />}
                            <div className="min-w-0">
                              <p className={`text-sm font-semibold ${
                                isCurrent ? "text-green-700"
                                : blockedByWeekDisabled ? "text-gray-400 line-through"
                                : (blockedByDramaLock || blockedByDramaJoin || blockedByOnceOnly || (isUsed && !allowRepeat)) ? "text-gray-400 line-through"
                                : blockedByPriority ? "text-gray-400"
                                : "text-foreground"
                              }`}>
                                {act}
                              </p>
                              {isCurrent && (
                                <p className="text-[10px] text-green-600 mt-0.5 font-medium">Current pick ✓</p>
                              )}
                              {blockedByWeekDisabled && (
                                <p className="text-[10px] text-rose-600 mt-0.5 font-medium">Not offered Week {currentWeekNum}</p>
                              )}
                              {!isCurrent && !blockedByWeekDisabled && isUsed && !allowRepeat && (
                                <p className="text-[10px] text-gray-400 mt-0.5">Already chosen - Week {usedWeek}</p>
                              )}
                              {!isCurrent && !blockedByWeekDisabled && blockedByOnceOnly && (
                                <p className="text-[10px] text-amber-600 mt-0.5">One-time only · taken Week {usedWeek}</p>
                              )}
                              {!blockedByWeekDisabled && blockedByDramaLock && (
                                <p className="text-[10px] text-gray-400 mt-0.5">Drama locked for all 6 weeks</p>
                              )}
                              {!blockedByWeekDisabled && blockedByDramaJoin && (
                                <p className="text-[10px] text-rose-500 mt-0.5">Drama is Week 1 only — closed for new joiners</p>
                              )}
                              {!isCurrent && !isUsed && !blockedByWeekDisabled && !blockedByDramaLock && !blockedByOnceOnly && isOnceOnly && (
                                <p className="text-[10px] text-amber-600 mt-0.5">One-time pick — counts for the summer</p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Past selections summary */}
        {selections.length > 0 && (
          <Card>
            <CardContent className="p-3">
              <h3 className="text-xs font-bold text-muted-foreground uppercase mb-2">Selection History</h3>
              <div className="space-y-1.5">
                {selections
                  .filter((s) => s.group === (user?.group_code || "").toUpperCase())
                  .sort((a, b) => a.week_number - b.week_number || a.camper_name.localeCompare(b.camper_name))
                  .map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-xs px-2 py-1.5 rounded-lg bg-muted/30">
                      <span className="font-medium">{s.camper_name}</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">Wk {s.week_number}</Badge>
                        <span className="text-violet-600 font-medium">{s.activity_selected}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
      <div className="h-20" />

      {/* Capacity-full warning popup — Navigator hobbies & intensives only */}
      <AlertDialog open={!!capWarning} onOpenChange={(o) => !o && setCapWarning(null)}>
        <AlertDialogContent data-testid="hobby-cap-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" />
              {capWarning?.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm pt-1">
              {capWarning?.body}
              <span className="block mt-2 text-xs text-muted-foreground">
                Pick a different activity, or talk to your supervisor if this camper needs an exception.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setCapWarning(null)} data-testid="hobby-cap-warning-ok">
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
